import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { QualityDocs } from "../components/QualityDocs";
import { CATEGORIES, IMAGES } from "../data/products";

const CATEGORY_IMAGES = {
  caramel: { src: "/images/caramel-colors.png", poster: true },
  flavours: { src: "/images/food-flavours.png", poster: true },
  ingredients: { src: IMAGES.scoop, poster: false },
};

const ROW_ACCENT = {
  caramel: "hover:border-l-[#E57A3C]",
  flavours: "hover:border-l-[#3E9B4F]",
  ingredients: "hover:border-l-[#F5B82A]",
};

export default function Products() {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const current = CATEGORIES.find((c) => c.id === active);

  return (
    <main data-testid="products-page" className="pt-[72px]">
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-20 sm:pt-32 pb-16">
        <p className="overline-label mb-8">The Catalogue</p>
        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[clamp(3rem,9vw,8rem)]" data-testid="products-heading">
          <MaskedLine delay={0.1}>EVERY POWDER,</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97736] via-[#F5B82A] to-[#F25C05]">ON SPEC.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-12 max-w-2xl text-base sm:text-lg leading-relaxed text-[#f5f5f5]/70">
            Twenty-two SKUs across caramel colours, food flavours and
            industrial ingredients — manufactured and exported from Ahmedabad.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-36">
        <Reveal>
          <div className="flex flex-wrap gap-3 mb-14" data-testid="category-tabs">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                data-testid={`tab-${cat.id}`}
                onClick={() => setActive(cat.id)}
                style={
                  active === cat.id
                    ? {
                        backgroundColor: cat.color,
                        borderColor: cat.color,
                        color: cat.textTone === "light" ? "#ffffff" : "#0a0a0a",
                      }
                    : undefined
                }
                className={`font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-6 py-3 rounded-full border transition-colors duration-300 ${
                  active === cat.id
                    ? ""
                    : "border-white/20 text-[#f5f5f5]/70 hover:border-[#D97736] hover:text-[#D97736]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4 lg:order-2">
            <div className="lg:sticky lg:top-28">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {CATEGORY_IMAGES[active].poster ? (
                    <div className="p-2 sm:p-3 bg-white/5 border border-white/10 rounded-2xl" data-testid="category-spotlight">
                      <div className="relative overflow-hidden rounded-xl">
                        <img
                          src={CATEGORY_IMAGES[active].src}
                          alt={current.label}
                          className="w-full h-auto"
                        />
                        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_55%,rgba(10,10,10,0.35)_100%)]" />
                      </div>
                      <div className="px-3 py-3">
                        <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em]" style={{ color: current.color }}>{current.code} series</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative overflow-hidden clip-frame h-[300px] lg:h-[540px]" data-testid="category-spotlight">
                      <img
                        src={CATEGORY_IMAGES[active].src}
                        alt={current.label}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/25" />
                      <div className="absolute bottom-0 left-0 p-6">
                        <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em]" style={{ color: current.color }}>{current.code} series</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:col-span-8 lg:order-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
            <p className="max-w-2xl text-sm sm:text-base leading-relaxed text-[#f5f5f5]/60 mb-4" data-testid="category-blurb">
              {current.blurb}
            </p>
            <Link
              to="/quality"
              data-testid="view-specs-link"
              className="group inline-flex items-center gap-2 font-mono-jb text-xs uppercase tracking-[0.2em] text-[#D97736] mb-12 hover:text-[#e8904f] transition-colors duration-300"
            >
              View product-wise COA &amp; MSDS
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <div className="border-t border-white/10" data-testid="product-list">
              {current.products.map((p, i) => (
                <Link
                  key={p.name}
                  to={`/contact?product=${encodeURIComponent(p.name)}`}
                  data-testid={`product-row-${current.id}-${i}`}
                  className={`group grid grid-cols-[auto_1fr_auto] sm:grid-cols-[80px_1fr_auto_auto] items-center gap-5 sm:gap-10 px-2 sm:px-6 py-7 border-b border-white/10 border-l-2 border-l-transparent transition-colors duration-500 hover:bg-[#111111] ${ROW_ACCENT[current.id]}`}
                >
                  <span className="font-mono-jb text-xs text-[#f5f5f5]/40">
                    {current.code}/{String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl sm:text-3xl font-bold tracking-tight transition-all duration-500 group-hover:translate-x-2 group-hover:text-[#D97736]">
                    {p.name}
                  </span>
                  <span className="hidden sm:block font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#f5f5f5]/50 border border-white/15 rounded-full px-4 py-1.5">
                    {p.form}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-[#f5f5f5]/30 transition-all duration-300 group-hover:text-[#D97736] group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </div>
          </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 border border-white/10 bg-[#1E3F33] p-10 sm:p-14">
            <div>
              <h3 className="font-display text-2xl sm:text-4xl font-bold tracking-tight mb-3">
                Need a custom mesh size or private label?
              </h3>
              <p className="text-sm text-[#f5f5f5]/65 max-w-lg">
                We mill to order. Tell us your specification and target volume — we'll respond with samples and pricing.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="products-cta-quote"
              className="group shrink-0 flex items-center gap-3 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#e8904f]"
            >
              Request a quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </section>

      <QualityDocs />
    </main>
  );
}
